import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const DEFAULT_ALLOWED_ROLES = ["doctor", "patient", "admin"];

const RequireAuth = ({
  children,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
  requireDoctorApproval = false
}) => {
  const [user, loading] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [doctorStatus, setDoctorStatus] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    const checkUserRole = async () => {
      if (!user || !user.emailVerified) {
        if (active) {
          setRole(null);
          setDoctorStatus(null);
          setCheckingRole(false);
        }
        return;
      }

      setCheckingRole(true);
      try {
        // First check if user is a pure admin (in admins collection)
        const adminSnap = await getDoc(doc(db, "admins", user.uid));
        if (!active) return;

        if (adminSnap.exists()) {
          setRole("admin");
          setDoctorStatus("approved");
          return;
        }

        // Then check if user is in doctors collection
        const doctorSnap = await getDoc(doc(db, "doctors", user.uid));
        if (!active) return;

        if (doctorSnap.exists()) {
          const data = doctorSnap.data() || {};
          // Check if doctor has admin role
          if (data.role === "admin") {
            setRole("admin");
            setDoctorStatus("approved");
            return;
          } else {
            setRole("doctor");
            setDoctorStatus(data.verificationStatus || "pending");
            return;
          }
        }

        // If not a doctor, check if patient
        const patientSnap = await getDoc(doc(db, "patients_auth", user.uid));
        if (!active) return;
        if (patientSnap.exists()) {
          setRole("patient");
          setDoctorStatus(null);
          return;
        }

        setRole(null);
        setDoctorStatus(null);
      } catch (err) {
        console.error("Role check failed:", err);
        if (active) {
          setRole(null);
          setDoctorStatus(null);
        }
      } finally {
        if (active) {
          setCheckingRole(false);
        }
      }
    };

    checkUserRole();
    return () => {
      active = false;
    };
  }, [user]);

  const needsVerification = user && !user.emailVerified;
  const isOnVerifyPage = location.pathname === "/verify-email";
  const isOnPendingPage = location.pathname === "/doctor/pending-approval";

  if (loading || checkingRole) {
    return <div className="p-10">Checking access...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsVerification && !isOnVerifyPage) {
    return (
      <Navigate
        to="/verify-email"
        state={{ email: user.email, from: location }}
        replace
      />
    );
  }

  if (needsVerification && isOnVerifyPage) {
    return children;
  }

  if (role && !allowedRoles.includes(role)) {
    if (role === "patient") {
      return <Navigate to="/patient/portal" replace />;
    }
    if (role === "doctor") {
      return <Navigate to="/dashboard" replace />;
    }
    if (role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (
    role === "doctor" &&
    doctorStatus !== "approved" &&
    requireDoctorApproval &&
    !isOnPendingPage
  ) {
    return <Navigate to="/doctor/pending-approval" replace />;
  }

  if (role === "doctor" && doctorStatus !== "approved" && isOnPendingPage) {
    return children;
  }

  return children;
};

export default RequireAuth;
